import { hasAnalyticsConsent } from "./consent";
import { getApiBaseCandidates } from "./apiClient";

// Absolute CF endpoint for high-value analytics collector
export const resolveAnalyticsCollector = ({ hostname = "", projectId = "" } = {}) => {
  if (/^(localhost|127\.0\.0\.1)$/i.test(hostname)) return null;
  const staging = /(^|[.-])staging([.-]|$)/i.test(hostname);
  if (staging || projectId === "zedipass-staging") {
    return "https://europe-west2-zedipass-staging.cloudfunctions.net/api/analytics/collect";
  }
  if (projectId === "zedipass" && /(^|\.)zedipass\.com$|^zedipass\.web\.app$|^zedipass\.firebaseapp\.com$/i.test(hostname)) {
    return "https://europe-west2-zedipass.cloudfunctions.net/api/analytics/collect";
  }
  return null;
};

// The GA property is production-only. Staging can use its own collector after consent.
export const canUseProductionGa = (hostname) =>
  ["zedipass.com", "www.zedipass.com", "zedipass.web.app", "zedipass.firebaseapp.com"].includes(String(hostname || "").toLowerCase());

export const GA_MEASUREMENT_ID = "G-W1C89G8BN4";
export const ANALYTICS_EXCLUSION_STORAGE_KEY = "zedi_analytics_excluded_v1";
const ANALYTICS_EXCLUSION_QUERY_KEYS = [
  "zedi_exclude_analytics",
  "zedi_analytics_exclude",
  "analytics_exclude",
  "internal_traffic",
];
const ATTRIBUTION_STORAGE_KEY = "zedi_attribution_v1";
const FIRST_TOUCH_STORAGE_KEY = "zedi_first_touch_attribution_v1";

const ATTRIBUTION_PARAM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "campaign_id",
  "source",
  "medium",
  "campaign",
  "channel",
  "scan",
  "scan_type",
  "qr",
  "qr_id",
  "card_id",
  "flyer_id",
];

export const EVENT_NAMES = {
  appDownload: {
    impression: "app_download_badges_impression",
    click: "app_download_click",
  },
  pilot: {
    invite_click: "pilot_invite_click",
    apply_click: "pilot_apply_click",
    intro_view: "pilot_intro_view",
    step_view: "pilot_step_view",
    form_submit: "pilot_form_submit",
    form_error: "pilot_form_error",
    form_dropoff: "pilot_form_dropoff",
    mou_interaction: "pilot_mou_interaction",
    cta_click: "pilot_register_cta",
  },
  pricing: {
    selector_view: "pricing_selector_view",
    plan_select: "pricing_plan_select",
    billing_cycle_select: "pricing_billing_cycle_select",
    demo_request: "pricing_demo_request",
  },
  onboarding: {
    step_view: "onboarding_step_view",
    step_complete: "onboarding_step_complete",
    step_advance: "onboarding_step_advance",
    step_back: "onboarding_step_back",
    flow_exit: "onboarding_flow_exit",
  },
  investor: {
    tab_view: "investor_tab_view",
    deck_download: "investor_deck_download",
    deck_present: "investor_deck_present",
  },
};

const getPrimaryApiBase = () => {
  const candidates = getApiBaseCandidates();
  return candidates[0] || '/api';
};


// Safety net: on production domains, rewrite any site-relative `/api/*` calls to Cloud Functions
if (typeof window !== "undefined" && typeof window.fetch === "function") {
  try {
    const originalFetch = window.fetch.bind(window);
    const isProdHost = (host) => /(^|\.)zedipass\.com$/i.test(host) || /(^|\.)zedi-pass\.co\.uk$/i.test(host);

    window.fetch = (input, init) => {
      try {
        const host = window.location && window.location.hostname;
        if (isProdHost(host)) {
          const base = getPrimaryApiBase();
          let url = typeof input === "string" ? input : (input && input.url);
          if (url) {
            // Resolve to absolute URL for comparison
            const abs = new URL(url, window.location.origin).href;
            const originApiPrefix = `${window.location.origin}/api/`;
            if (abs.startsWith(originApiPrefix)) {
              const path = abs.substring(originApiPrefix.length); // everything after `/api/`
              const cfUrl = `${base}/${path}`;
              if (typeof input === "string") {
                input = cfUrl;
              } else if (input && typeof Request !== "undefined" && input instanceof Request) {
                input = new Request(cfUrl, input);
              }
            }
          }
        }
      } catch (_) {}
      return originalFetch(input, init);
    };
  } catch (_) {}
}

// Safety net: also rewrite navigator.sendBeacon calls to `/api/*` in production
if (typeof window !== "undefined" && typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
  try {
    const isProdHost = (host) => /(^|\.)zedipass\.com$/i.test(host) || /(^|\.)zedi-pass\.co\.uk$/i.test(host);
    const originalSendBeacon = navigator.sendBeacon.bind(navigator);
    navigator.sendBeacon = (url, data) => {
      try {
        const host = window.location && window.location.hostname;
        if (isProdHost(host) && typeof url === "string") {
          const abs = new URL(url, window.location.origin).href;
          const originApiPrefix = `${window.location.origin}/api/`;
          if (abs.startsWith(originApiPrefix)) {
            const path = abs.substring(originApiPrefix.length); // everything after `/api/`
            const cfUrl = `${getPrimaryApiBase()}/${path}`;
            return originalSendBeacon(cfUrl, data);
          }
        }
      } catch (_) {}
      return originalSendBeacon(url, data);
    };
  } catch (_) {}
}

// Safety net: rewrite XMLHttpRequest calls to `/api/*` in production (covers axios and raw XHR)
if (typeof window !== "undefined" && typeof XMLHttpRequest !== "undefined") {
  try {
    const isProdHost = (host) => /(^|\.)zedipass\.com$/i.test(host) || /(^|\.)zedi-pass\.co\.uk$/i.test(host);
    const originalOpen = XMLHttpRequest.prototype.open;

    XMLHttpRequest.prototype.open = function(method, url, async, user, password) {
      try {
        const host = window.location && window.location.hostname;
        if (isProdHost(host) && typeof url === "string") {
          const abs = new URL(url, window.location.origin).href;
          const originApiPrefix = `${window.location.origin}/api/`;
          if (abs.startsWith(originApiPrefix)) {
            const path = abs.substring(originApiPrefix.length); // everything after `/api/`
            const cfUrl = `${getPrimaryApiBase()}/${path}`;
            return originalOpen.call(this, method, cfUrl, async, user, password);
          }
        }
      } catch (_) {}
      return originalOpen.call(this, method, url, async, user, password);
    };
  } catch (_) {}
}

const getLocale = () => {
  if (typeof window === "undefined") {
    return "unknown";
  }

  try {
    const stored = window.localStorage?.getItem?.("zedi_locale");
    if (stored) {
      return stored;
    }
  } catch (error) {
    // Ignore storage access errors (Safari ITP, private mode, etc.)
  }

  if (typeof navigator !== "undefined") {
    return navigator.language || navigator.languages?.[0] || "unknown";
  }

  return "unknown";
};

// Never include verification codes, checkout sessions, form values or URL queries.
export const analyticsUrl = (value, pathOnly = false) => {
  if (!value) return "";
  try {
    const url = new URL(value, window.location.origin);
    if (!/^https?:$/.test(url.protocol)) return "";
    return pathOnly ? url.pathname : `${url.origin}${url.pathname}`;
  } catch (_) { return ""; }
};

const sanitizeParams = (params = {}) => Object.fromEntries(
  Object.entries(params).flatMap(([key, value]) => {
    if (/email|password|token|signature|message|contact_link|^uid$|^user_id$|owner_name|business_name/i.test(key)) return [];
    if (value == null || typeof value === "object") return [];
    if (/^(?:page_location|page_referrer|referrer|outbound_url|link_url|destination_url|landing_page|last_landing_page|first_landing_page|page_path)$/.test(key) && typeof value === "string") {
      return [[key, analyticsUrl(value, /page_path|landing_page/.test(key))]];
    }
    if (typeof value === "string" && /@|[?&](?:oobCode|token|email|session_id)=/i.test(value)) return [];
    return [[key, typeof value === "string" ? value.slice(0, 180) : value]];
  })
);

export const initializeAnalytics = () => {
  if (typeof window === "undefined" || !shouldUseAnalytics(true)) return false;
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
  window.gtag("consent", "update", { analytics_storage: "granted" });
  if (!window.__zediGaInitialized) {
    window.gtag("config", GA_MEASUREMENT_ID, {
      send_page_view: false,
      page_location: analyticsUrl(window.location.href),
      page_referrer: analyticsUrl(document.referrer),
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    });
    const script = document.createElement("script");
    script.id = "zedi-google-analytics";
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);
    window.__zediGaInitialized = true;
  }
  return true;
};

const getPageLocationContext = () => {
  if (typeof window === "undefined") {
    return { page_path: undefined, page_title: undefined };
  }

  const pathname = window.location?.pathname || "/";
  return {
    page_path: pathname,
    page_title: typeof document !== "undefined" ? document?.title : undefined,
  };
};

const safeStorageGet = (storage, key) => {
  try {
    const value = storage?.getItem?.(key);
    return value ? JSON.parse(value) : null;
  } catch (_) {
    return null;
  }
};

const safeStorageSet = (storage, key, value) => {
  try {
    storage?.setItem?.(key, JSON.stringify(value));
  } catch (_) {}
};

const safeStorageSetRaw = (storage, key, value) => {
  try {
    storage?.setItem?.(key, value);
  } catch (_) {}
};

const applyAnalyticsExclusionFlag = (excluded) => {
  if (typeof window === "undefined") return;

  try {
    safeStorageSetRaw(window.localStorage, ANALYTICS_EXCLUSION_STORAGE_KEY, excluded ? "true" : "false");
    window[`ga-disable-${GA_MEASUREMENT_ID}`] = Boolean(excluded);
  } catch (_) {}
};

export const setAnalyticsExcluded = (excluded = true) => {
  applyAnalyticsExclusionFlag(Boolean(excluded));
};

const applyAnalyticsExclusionFromUrl = () => {
  if (typeof window === "undefined") return null;

  try {
    const searchParams = new URLSearchParams(window.location?.search || "");
    for (const key of ANALYTICS_EXCLUSION_QUERY_KEYS) {
      if (!searchParams.has(key)) continue;

      const raw = String(searchParams.get(key) || "").trim().toLowerCase();
      const excluded = !["0", "false", "off", "no"].includes(raw);
      applyAnalyticsExclusionFlag(excluded);
      return excluded;
    }
  } catch (_) {}

  return null;
};

applyAnalyticsExclusionFromUrl();

const normalizeAttributionValue = (value, max = 180) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  if (!normalized) return null;
  return normalized.length > max ? normalized.slice(0, max) : normalized;
};

const inferScanSource = (params) => {
  const scanType = normalizeAttributionValue(params.scan_type || params.scan || params.qr);
  if (scanType) return scanType;
  if (params.flyer_id) return "flyer";
  if (params.card_id) return "business_card";
  return null;
};

const normalizeAttributionParams = (searchParams) => {
  const out = {};
  ATTRIBUTION_PARAM_KEYS.forEach((key) => {
    const value = normalizeAttributionValue(searchParams.get(key));
    if (value) out[key] = value;
  });

  if (!out.utm_source && out.source) out.utm_source = out.source;
  if (!out.utm_medium && out.medium) out.utm_medium = out.medium;
  if (!out.utm_campaign && out.campaign) out.utm_campaign = out.campaign;

  const scanSource = inferScanSource(out);
  if (scanSource) {
    out.scan_source = scanSource;
    if (!out.utm_medium) out.utm_medium = "qr";
    if (!out.utm_source) out.utm_source = scanSource;
  }

  return out;
};

export const getAttributionContext = () => {
  if (typeof window === "undefined") return {};

  const location = window.location;
  const now = new Date().toISOString();
  const searchParams = new URLSearchParams(location?.search || "");
  const urlAttribution = normalizeAttributionParams(searchParams);
  const hasUrlAttribution = Object.keys(urlAttribution).length > 0;
  const referrer = analyticsUrl(document?.referrer);

  const storedLast = safeStorageGet(window.localStorage, ATTRIBUTION_STORAGE_KEY) || {};
  const storedFirst = safeStorageGet(window.localStorage, FIRST_TOUCH_STORAGE_KEY) || {};
  const landingPage = location?.pathname || "/";
  const current = hasUrlAttribution
    ? {
        ...storedLast,
        ...urlAttribution,
        landing_page: storedLast.landing_page || landingPage,
        last_landing_page: landingPage,
        referrer: referrer || storedLast.referrer || null,
        first_seen_at: storedLast.first_seen_at || now,
        last_seen_at: now,
      }
    : {
        ...storedLast,
        referrer: storedLast.referrer || referrer || null,
        landing_page: storedLast.landing_page || landingPage,
        first_seen_at: storedLast.first_seen_at || now,
        last_seen_at: now,
      };

  if (hasUrlAttribution || !storedLast.landing_page) {
    safeStorageSet(window.localStorage, ATTRIBUTION_STORAGE_KEY, current);
  }

  if (hasUrlAttribution && !storedFirst.landing_page) {
    safeStorageSet(window.localStorage, FIRST_TOUCH_STORAGE_KEY, {
      ...urlAttribution,
      landing_page: landingPage,
      referrer: referrer || null,
      first_seen_at: now,
    });
  }

  const first = safeStorageGet(window.localStorage, FIRST_TOUCH_STORAGE_KEY) || storedFirst;

  return {
    ...current,
    first_utm_source: first.utm_source || null,
    first_utm_medium: first.utm_medium || null,
    first_utm_campaign: first.utm_campaign || null,
    first_scan_source: first.scan_source || null,
    first_landing_page: first.landing_page || null,
  };
};

export const buildEventContext = ({ planId, locale, consent } = {}) => {
  const consentState = consent ?? (hasAnalyticsConsent() ? "granted" : "denied");
  const attribution = getAttributionContext();
  const baseContext = {
    consent_state: consentState,
    locale: locale || getLocale(),
    plan_id: planId || null,
    ...attribution,
  };

  return {
    ...baseContext,
    ...getPageLocationContext(),
  };
};

const generateEventId = () => {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch (_) {}
  return `evt_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const resolveSessionId = () => {
  if (typeof window === "undefined") return null;
  try {
    const key = "zedi_analytics_session_id_v1";
    const existing = window.sessionStorage.getItem(key);
    if (existing) return existing;
    const created = generateEventId();
    window.sessionStorage.setItem(key, created);
    return created;
  } catch (_) {
    return null;
  }
};

const buildEnvelope = (eventName, payload, context) => ({
  event_name: eventName,
  event_id: generateEventId(),
  occurred_at: new Date().toISOString(),
  ingested_at: new Date().toISOString(),
  schema_version: 1,
  source_app: "signupwebsite",
  tenant_id: context?.tenant_id || null,
  retailer_id: context?.retailer_id || null,
  uid_hash: null,
  uid: null,
  session_id: resolveSessionId(),
  consent_state: context?.consent_state || "unknown",
  policy_version: context?.policy_version || "v1",
  platform: "web",
  app_version: process.env.REACT_APP_VERSION || null,
  locale: context?.locale || null,
  timezone:
    typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone || null : null,
  campaign_id: context?.campaign_id || payload?.campaign_id || null,
  utm_source: context?.utm_source || payload?.utm_source || null,
  utm_medium: context?.utm_medium || payload?.utm_medium || null,
  utm_campaign: context?.utm_campaign || payload?.utm_campaign || null,
  utm_content: context?.utm_content || payload?.utm_content || null,
  utm_term: context?.utm_term || payload?.utm_term || null,
  scan_source: context?.scan_source || payload?.scan_source || null,
  qr_id: context?.qr_id || payload?.qr_id || null,
  card_id: context?.card_id || payload?.card_id || null,
  flyer_id: context?.flyer_id || payload?.flyer_id || null,
  landing_page: context?.landing_page || payload?.landing_page || null,
  first_utm_source: context?.first_utm_source || payload?.first_utm_source || null,
  first_utm_medium: context?.first_utm_medium || payload?.first_utm_medium || null,
  first_utm_campaign: context?.first_utm_campaign || payload?.first_utm_campaign || null,
  first_scan_source: context?.first_scan_source || payload?.first_scan_source || null,
  first_landing_page: context?.first_landing_page || payload?.first_landing_page || null,
});

const sendToCollector = async (eventName, payload, context) => {
  if (typeof window === "undefined") return;
  if (isAnalyticsExcluded()) return;

  try {
    const host = window.location && window.location.hostname;
    const isLocal = /^(localhost|127\.0\.0\.1)$/i.test(host);

    // In local/dev, skip hitting the collector to avoid 404 noise
    if (isLocal) return;

    const endpoint = resolveAnalyticsCollector({
      hostname: host,
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "",
    });

    // If endpoint looks relative, skip (collector must be absolute)
    if (typeof endpoint !== 'string' || !/^https?:\/\//i.test(endpoint)) return;

    const envelope = buildEnvelope(eventName, payload, context);
    const body = JSON.stringify({
      ...envelope,
      event: eventName,
      payload,
      context,
      timestamp: envelope.occurred_at,
    });

    // Prefer fetch to avoid opaque sendBeacon failures surfacing as 404s in console
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body,
    });
    if (!res.ok) {
      // Swallow 404 quietly; log others at debug level
      if (res.status !== 404) {
        console.debug('[analytics] collector returned', res.status);
      }
    }
  } catch (error) {
    // Quietly ignore network errors (adblock, offline, etc.)
    const msg = String(error && (error.message || error)).toLowerCase();
    const ignorable = msg.includes('failed to fetch') || msg.includes('network');
    if (!ignorable) {
      console.debug('[analytics] failed to send to collector', error);
    }
  }
};

const emitEventWithContext = (
  eventName,
  payload = {},
  { planId, locale, consent, requireConsent = true, highValue = false } = {}
) => {
  if (isAnalyticsExcluded() || !hasAnalyticsConsent()) return;
  const context = sanitizeParams(buildEventContext({ planId, locale, consent }));
  const enrichedPayload = {
    ...context,
    ...sanitizeParams(payload),
  };

  logEvent(eventName, enrichedPayload, { requireConsent });

  if (highValue && context.consent_state === "granted") {
    void sendToCollector(eventName, enrichedPayload, context);
  }
};

const ROUTE_METADATA = [
  { pattern: /^\/contact$/, data: { page_name: "commercial_enquiry", page_category: "conversion" } },
  { pattern: /^\/for-shops$/, data: { page_name: "for_shops", page_category: "conversion" } },
  { pattern: /^\/discover$/, data: { page_name: "discover", page_category: "conversion" } },
  { pattern: /^\/enterprise$/, data: { page_name: "enterprise", page_category: "conversion" } },
  { pattern: /^\/$/, data: { page_name: "marketing_home", page_category: "marketing" } },
  { pattern: /^\/home$/, data: { page_name: "marketing_home", page_category: "marketing" } },
  { pattern: /^\/product$/, data: { page_name: "product_overview", page_category: "marketing" } },
  { pattern: /^\/solutions$/, data: { page_name: "solutions_overview", page_category: "marketing" } },
  { pattern: /^\/pricing$/, data: { page_name: "pricing", page_category: "conversion" } },
  { pattern: /^\/loyalty-program$/, data: { page_name: "loyalty_program", page_category: "marketing" } },
  { pattern: /^\/white-label-referral$/, data: { page_name: "white_label_referral", page_category: "conversion" } },
  { pattern: /^\/resources(\/.*)?$/, data: { page_name: "resources", page_category: "content" } },
  { pattern: /^\/signup$/, data: { page_name: "signup", page_category: "conversion" } },
  { pattern: /^\/signup\/whitelabel$/, data: { page_name: "whitelabel_solutions", page_category: "conversion" } },
  { pattern: /^\/signup\/whitelabel\/standard$/, data: { page_name: "whitelabel_standard_signup", page_category: "conversion" } },
  { pattern: /^\/signup\/whitelabel\/multibrand$/, data: { page_name: "whitelabel_multibrand_signup", page_category: "conversion" } },
  { pattern: /^\/signup\/whitelabel\/luxury$/, data: { page_name: "whitelabel_luxury_signup", page_category: "conversion" } },
  { pattern: /^\/register$/, data: { page_name: "register_interest", page_category: "conversion" } },
  { pattern: /^\/success$/, data: { page_name: "checkout_success", page_category: "conversion" } },
  { pattern: /^\/login$/, data: { page_name: "login", page_category: "auth" } },
  { pattern: /^\/verify-email$/, data: { page_name: "verify_email", page_category: "auth" } },
  { pattern: /^\/forgot-password$/, data: { page_name: "forgot_password", page_category: "auth" } },
  { pattern: /^\/partners(\/.*)?$/, data: { page_name: "partners", page_category: "content" } },
  { pattern: /^\/partners2$/, data: { page_name: "partners_preview", page_category: "content" } },
  { pattern: /^\/pilot(\/.*)?$/, data: { page_name: "pilot_program", page_category: "content" } },
  { pattern: /^\/pilot-testing$/, data: { page_name: "pilot_testing_guide", page_category: "content" } },
  { pattern: /^\/friends-family$/, data: { page_name: "friends_family_portal", page_category: "content" } },
  { pattern: /^\/findyourfit$/, data: { page_name: "find_your_fit", page_category: "marketing" } },
  { pattern: /^\/business-showcase$/, data: { page_name: "business_showcase", page_category: "marketing" } },
  { pattern: /^\/faq$/, data: { page_name: "faq", page_category: "support" } },
  { pattern: /^\/terms$/, data: { page_name: "terms_of_service", page_category: "legal" } },
  { pattern: /^\/gdpr$/, data: { page_name: "gdpr_compliance", page_category: "legal" } },
  { pattern: /^\/privacy$/, data: { page_name: "privacy_policy", page_category: "legal" } },
  { pattern: /^\/cookies$/, data: { page_name: "cookie_policy", page_category: "legal" } },
  { pattern: /^\/onboardingoverview$/, data: { page_name: "onboarding_overview", page_category: "app", requiresAuth: true } },
  { pattern: /^\/onboardingOverview$/, data: { page_name: "onboarding_overview_redirect", page_category: "app", requiresAuth: true } },
  { pattern: /^\/onboardingsetup$/, data: { page_name: "onboarding_setup", page_category: "app", requiresAuth: true } },
  { pattern: /^\/business-setup$/, data: { page_name: "onboarding_setup", page_category: "app", requiresAuth: true } },
  { pattern: /^\/setup-complete$/, data: { page_name: "setup_complete", page_category: "app", requiresAuth: true } },
  { pattern: /^\/payment$/, data: { page_name: "payment", page_category: "billing", requiresAuth: true } },
  { pattern: /^\/receipt$/, data: { page_name: "receipt", page_category: "billing", requiresAuth: true } },
  { pattern: /^\/billing$/, data: { page_name: "billing_portal", page_category: "billing", requiresAuth: true } },
  { pattern: /^\/admin\/approve-payment$/, data: { page_name: "admin_approve_payment", page_category: "internal", requiresAuth: true } },
  { pattern: /^\/myaccount$/, data: { page_name: "account_settings", page_category: "app", requiresAuth: true } },
];

export const getRouteAnalyticsMetadata = (pathname = "") => {
  const found = ROUTE_METADATA.find(({ pattern }) => pattern.test(pathname));
  return found ? found.data : { page_name: "unknown", page_category: "unknown" };
};

export const isAnalyticsExcluded = () => {
  if (typeof window === "undefined") {
    return false;
  }

  const urlValue = applyAnalyticsExclusionFromUrl();
  if (urlValue !== null) {
    return urlValue;
  }

  try {
    const excluded = window.localStorage?.getItem?.(ANALYTICS_EXCLUSION_STORAGE_KEY) === "true";
    window[`ga-disable-${GA_MEASUREMENT_ID}`] = excluded;
    return excluded;
  } catch (_) {
    return false;
  }
};

const shouldUseAnalytics = (requireConsent) => {
  if (typeof window !== "undefined" && !canUseProductionGa(window.location?.hostname)) return false;
  if (isAnalyticsExcluded()) {
    return false;
  }

  return hasAnalyticsConsent();
};

const getPageContext = () => {
  if (typeof window === "undefined") {
    return {};
  }

  const pathname = window.location?.pathname || "/";
  const metadata = getRouteAnalyticsMetadata(pathname);

  return {
    page_name: metadata?.page_name,
    page_category: metadata?.page_category,
    page_path: pathname,
    page_location: analyticsUrl(window.location?.href),
    ...getAttributionContext(),
  };
};

const withDefaultContext = (params = {}, options = {}) => {
  if (options?.includePageMetadata === false) {
    return { ...params };
  }

  const context = getPageContext();

  return {
    ...context,
    ...params,
  };
};

export const pushToDataLayer = (eventName, params = {}, options = {}) => {
  if (typeof window === "undefined") {
    return;
  }

  if (!eventName) {
    return;
  }

  const { requireConsent = true } = options;
  if (!shouldUseAnalytics(requireConsent)) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    ...sanitizeParams(withDefaultContext(params, options)),
  });
};

export const trackEvent = (eventName, params = {}, options = {}) => {
  if (!eventName || !shouldUseAnalytics(options.requireConsent !== false)) {
    return undefined;
  }

  const payload = sanitizeParams(withDefaultContext(params, options));

  logEvent(eventName, payload, options);
  pushToDataLayer(eventName, payload, options);

  return payload;
};

export const logFunnelStep = ({
  funnelName,
  stepName,
  stepNumber,
  status,
  action,
  extra = {},
  options = {},
} = {}) => {
  return trackEvent(
    "funnel_step",
    {
      funnel_name: funnelName,
      funnel_step_name: stepName,
      funnel_step_number: typeof stepNumber === "number" ? stepNumber : undefined,
      funnel_step_status: status,
      funnel_step_action: action,
      ...extra,
    },
    options
  );
};

export const logFormSubmission = ({
  formName,
  formId,
  status,
  errorMessage,
  durationMs,
  extra = {},
  options = {},
} = {}) => {
  const payload = {
    form_name: formName,
    form_id: formId,
    form_status: status,
    form_error: errorMessage,
    form_duration_ms: typeof durationMs === "number" ? Math.max(Math.round(durationMs), 0) : undefined,
    ...extra,
  };

  return trackEvent("form_submission", payload, options);
};

export const logEvent = (eventName, params = {}, options = {}) => {
  if (typeof window === "undefined" || typeof window.gtag !== "function") {
    return;
  }

  const { requireConsent = true } = options;
  if (!shouldUseAnalytics(requireConsent)) {
    return;
  }

  const enrichedParams = sanitizeParams(withDefaultContext(params, options));

  window.gtag("event", eventName, enrichedParams);
};

export const logPageView = ({
  pathname,
  search = "",
  title = "",
  metadata = {},
  user,
  referrer,
} = {}) => {
  if (typeof window === "undefined" || typeof window.gtag !== "function") {
    return undefined;
  }

  if (!shouldUseAnalytics(true)) {
    return undefined;
  }

  const page_path = pathname || window.location?.pathname || "/";
  const basePayload = {
    page_path,
    page_location: analyticsUrl(window.location?.href),
    page_title: title || document?.title || undefined,
    page_referrer: analyticsUrl(referrer ?? document.referrer),
    user_status: user ? "signed_in" : "anonymous",
    requires_auth: metadata?.requiresAuth ? "true" : "false",
    page_category: metadata?.page_category,
    page_name: metadata?.page_name,
  };

  window.gtag("config", GA_MEASUREMENT_ID, { ...basePayload, send_page_view: false });
  window.gtag("event", "page_view", basePayload);

  return basePayload;
};

export const logEngagement = ({
  payload,
  durationMs,
}) => {
  if (!payload || !durationMs || durationMs < 0) {
    return;
  }

  logEvent(
    "page_engagement",
    {
      ...payload,
      engagement_time_msec: Math.round(durationMs),
    },
    { requireConsent: true }
  );
};

export const logCtaClick = ({ name, location, pageName, extra = {} }) => {
  trackEvent("cta_click", {
    cta_name: name,
    cta_location: location,
    page_name: pageName,
    ...extra,
  });
};

export const logModalOpen = ({ modalName, source, pageName }) => {
  trackEvent("modal_open", {
    modal_name: modalName,
    modal_source: source,
    page_name: pageName,
  });
};

export const trackPilotPageView = (payload = {}, options = {}) => {
  emitEventWithContext(EVENT_NAMES.pilot.intro_view, payload, {
    ...options,
    highValue: true,
  });
};

export const trackAppDownloadImpression = (payload = {}, options = {}) => {
  emitEventWithContext(EVENT_NAMES.appDownload.impression, payload, {
    ...options,
    highValue: true,
  });
};

export const trackAppDownloadClick = (payload = {}, options = {}) => {
  emitEventWithContext(EVENT_NAMES.appDownload.click, payload, {
    ...options,
    highValue: true,
  });
};

export const trackPilotInviteClick = (payload = {}, options = {}) => {
  emitEventWithContext(EVENT_NAMES.pilot.invite_click, payload, {
    ...options,
    highValue: true,
  });
};

export const trackPilotApplyClick = (payload = {}, options = {}) => {
  emitEventWithContext(EVENT_NAMES.pilot.apply_click, payload, {
    ...options,
    highValue: true,
  });
};

export const trackPilotStepView = (payload = {}, options = {}) => {
  emitEventWithContext(EVENT_NAMES.pilot.step_view, payload, {
    ...options,
    highValue: true,
  });
};

export const trackPilotFormSubmit = (payload = {}, options = {}) => {
  emitEventWithContext(EVENT_NAMES.pilot.form_submit, payload, {
    ...options,
    highValue: true,
  });
};

export const trackPilotFormError = (payload = {}, options = {}) => {
  emitEventWithContext(EVENT_NAMES.pilot.form_error, payload, {
    ...options,
    highValue: true,
  });
};

export const trackPilotFormDropoff = (payload = {}, options = {}) => {
  emitEventWithContext(EVENT_NAMES.pilot.form_dropoff, payload, {
    ...options,
    highValue: true,
  });
};

export const trackPilotMouInteraction = (payload = {}, options = {}) => {
  emitEventWithContext(EVENT_NAMES.pilot.mou_interaction, payload, {
    ...options,
    highValue: true,
  });
};

export const trackPilotCtaClick = (payload = {}, options = {}) => {
  emitEventWithContext(EVENT_NAMES.pilot.cta_click, payload, {
    ...options,
    highValue: true,
  });
};

export const trackPricingSelectorView = (payload = {}, options = {}) => {
  emitEventWithContext(EVENT_NAMES.pricing.selector_view, payload, {
    ...options,
    highValue: true,
  });
};

export const trackPricingPlanSelect = ({ planId, ...payload } = {}, options = {}) => {
  emitEventWithContext(
    EVENT_NAMES.pricing.plan_select,
    {
      ...payload,
      plan_id: planId ?? payload?.plan_id ?? null,
    },
    {
      ...options,
      planId: planId ?? options?.planId,
      highValue: true,
    }
  );
};

export const trackPricingBillingCycleSelect = (payload = {}, options = {}) => {
  emitEventWithContext(EVENT_NAMES.pricing.billing_cycle_select, payload, {
    ...options,
    highValue: true,
  });
};

export const trackPricingDemoRequest = (payload = {}, options = {}) => {
  emitEventWithContext(EVENT_NAMES.pricing.demo_request, payload, {
    ...options,
    highValue: true,
  });
};

export const trackOnboardingStepView = ({ planId, ...payload } = {}, options = {}) => {
  emitEventWithContext(
    EVENT_NAMES.onboarding.step_view,
    {
      ...payload,
      plan_id: planId ?? payload?.plan_id ?? null,
    },
    {
      ...options,
      planId: planId ?? options?.planId,
      highValue: true,
    }
  );
};

export const trackOnboardingStepComplete = ({ planId, ...payload } = {}, options = {}) => {
  emitEventWithContext(
    EVENT_NAMES.onboarding.step_complete,
    {
      ...payload,
      plan_id: planId ?? payload?.plan_id ?? null,
    },
    {
      ...options,
      planId: planId ?? options?.planId,
      highValue: true,
    }
  );
};

export const trackOnboardingStepAdvance = ({ planId, ...payload } = {}, options = {}) => {
  emitEventWithContext(
    EVENT_NAMES.onboarding.step_advance,
    {
      ...payload,
      plan_id: planId ?? payload?.plan_id ?? null,
    },
    {
      ...options,
      planId: planId ?? options?.planId,
      highValue: true,
    }
  );
};

export const trackOnboardingStepBack = ({ planId, ...payload } = {}, options = {}) => {
  emitEventWithContext(
    EVENT_NAMES.onboarding.step_back,
    {
      ...payload,
      plan_id: planId ?? payload?.plan_id ?? null,
    },
    {
      ...options,
      planId: planId ?? options?.planId,
      highValue: true,
    }
  );
};

export const trackOnboardingFlowExit = ({ planId, ...payload } = {}, options = {}) => {
  emitEventWithContext(
    EVENT_NAMES.onboarding.flow_exit,
    {
      ...payload,
      plan_id: planId ?? payload?.plan_id ?? null,
    },
    {
      ...options,
      planId: planId ?? options?.planId,
      highValue: true,
    }
  );
};

export const trackInvestorTabView = (payload = {}, options = {}) => {
  emitEventWithContext(EVENT_NAMES.investor.tab_view, payload, {
    ...options,
    highValue: true,
  });
};

export const trackInvestorDeckDownload = (payload = {}, options = {}) => {
  emitEventWithContext(EVENT_NAMES.investor.deck_download, payload, {
    ...options,
    highValue: true,
  });
};

export const trackInvestorDeckPresent = (payload = {}, options = {}) => {
  emitEventWithContext(EVENT_NAMES.investor.deck_present, payload, {
    ...options,
    highValue: true,
  });
};

export function trackWebSummitVisit(source = "") { trackEvent("websummit_visit", { source }); }
export function trackWebSummitClick(action = "") { trackEvent("websummit_click", { action }); }
